import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Dashboard,
  MenuBook,
  CalendarMonth,
  Quiz,
  Assignment,
  School,
  BarChart,
  ChatBubbleOutline,
  CreditCard,
  Settings as SettingsIcon,
  ExitToApp,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import NotificationBell from '../../../components/NotificationBell';
import edumetrixRoundLogo from '../../../assets/edumetrix-round-logo.png';

/**
 * Student-portal top-navigation shell — mirrors the HTML mockup's `.topnav` /
 * `.wrap` / mobile `.bottomnav`. Replaces the shared left-drawer Layout for
 * student routes only (Admin/Teacher keep Layout.js). Presentation + navigation
 * only; auth/logout come from context.
 */
const NAV = [
  { label: 'Dashboard', path: '/student/dashboard', icon: <Dashboard /> },
  { label: 'My Classes', path: '/student/classes', icon: <MenuBook /> },
  { label: 'Calendar', path: '/student/calendar', icon: <CalendarMonth /> },
  { label: 'Quick Tests', path: '/student/quick-test', icon: <Quiz /> },
  { label: 'Homework', path: '/student/homework', icon: <Assignment /> },
  { label: 'Teachers', path: '/student/teachers', icon: <School /> },
  { label: 'Attendance', path: '/student/attendance', icon: <BarChart /> },
  { label: 'Chat', path: '/student/chat', icon: <ChatBubbleOutline /> },
  { label: 'Invoices', path: '/student/invoices', icon: <CreditCard /> },
];

// Primary destinations surfaced in the mobile bottom bar.
const BOTTOM = ['Dashboard', 'My Classes', 'Calendar', 'Homework', 'Chat'];

const StudentLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const displayName =
    user?.profile?.name || user?.profile?.studentName || user?.username || 'Student';
  const firstName = displayName.split(' ')[0];
  const initial = displayName.charAt(0).toUpperCase();
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  const navButtonSx = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    px: '14px',
    py: '8px',
    borderRadius: '9px',
    fontSize: '13.5px',
    fontWeight: active ? 700 : 600,
    whiteSpace: 'nowrap',
    color: active ? 'primary.main' : 'text.secondary',
    bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.13) : 'transparent',
    minWidth: 'auto',
    textTransform: 'none',
    transition: 'all 0.15s',
    '& svg': { fontSize: 18 },
    '&:hover': {
      bgcolor: active
        ? (t) => alpha(t.palette.primary.main, 0.18)
        : (t) => alpha(t.palette.text.primary, 0.05),
      color: active ? 'primary.main' : 'text.primary',
    },
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top navigation */}
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.88),
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            maxWidth: 1240,
            mx: 'auto',
            px: { xs: '14px', sm: '18px', md: '28px' },
            height: 64,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2.5 },
          }}
        >
          {/* Logo */}
          <Box
            onClick={() => navigate('/student/dashboard')}
            sx={{ display: 'flex', alignItems: 'center', gap: '11px', flex: 'none', cursor: 'pointer' }}
          >
            <Box
              component="img"
              src={edumetrixRoundLogo}
              alt="EduMetrix"
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                objectFit: 'cover',
                display: 'block',
                flex: 'none',
              }}
            />
            <Typography component="span" sx={{ fontWeight: 800, fontSize: 19, color: 'text.primary' }}>
              Edu<Box component="span" sx={{ color: 'primary.main' }}>Metrix</Box>
            </Typography>
          </Box>

          {/* Main nav (desktop) */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: '2px',
              height: '100%',
              overflowX: 'auto',
              flex: 1,
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {NAV.map((item) => (
              <Box
                key={item.path}
                component="button"
                onClick={() => navigate(item.path)}
                sx={{ border: 'none', cursor: 'pointer', ...navButtonSx(isActive(item.path)) }}
              >
                {item.icon}
                {item.label}
              </Box>
            ))}
          </Box>

          {/* Right cluster */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flex: 'none' }}>
            <NotificationBell userType={user?.userType} />
            <Tooltip title="Settings">
              <IconButton onClick={() => navigate('/student/settings')} sx={{ borderRadius: '12px' }}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Typography
              sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '13.5px', fontWeight: 600, mx: 0.5 }}
            >
              {firstName}
            </Typography>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
              <Avatar
                sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.9rem', fontWeight: 700 }}
              >
                {initial}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { borderRadius: '12px', mt: 1, minWidth: 180 } }}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate('/student/settings');
                }}
                sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
              >
                <SettingsIcon fontSize="small" sx={{ mr: 1.5 }} /> Settings
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={handleLogout}
                sx={{ borderRadius: '8px', mx: 1, my: 0.5, color: 'error.main' }}
              >
                <ExitToApp fontSize="small" sx={{ mr: 1.5 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box
        component="main"
        sx={{
          maxWidth: 1080,
          mx: 'auto',
          px: { xs: '12px', sm: '16px', md: '20px', lg: '28px' },
          pt: { xs: '18px', sm: '22px', md: '26px', lg: '32px' },
          pb: { xs: '96px', lg: '48px' },
        }}
      >
        {children}
      </Box>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            display: 'flex',
            bgcolor: (t) => alpha(t.palette.background.paper, 0.96),
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {NAV.filter((n) => BOTTOM.includes(n.label)).map((item) => {
            const active = isActive(item.path);
            return (
              <Box
                key={item.path}
                component="button"
                onClick={() => navigate(item.path)}
                sx={{
                  flex: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '3px',
                  py: '9px',
                  color: active ? 'primary.main' : 'text.secondary',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  '& svg': { fontSize: 20 },
                }}
              >
                {item.icon}
                {item.label}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default StudentLayout;
